import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Select, Tooltip } from 'antd';
import { useQuery } from 'react-query';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { countryStateCityApi } from 'services/apis';
import { uniqBy } from 'lodash';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { EQueryKeys } from 'constants/query-keys';
import { useTranslation } from 'react-i18next';
import { AddressFormInitialData } from 'components/ui/address-form/types';
import { SortedSelect } from 'components/ui/sorted-select';

interface AddressFormProps {
  countryName?: string;
  cityName?: string;
  postcodeName?: string;
  addressName?: string;
  stateName?: string;
  resetFormField: (field: string) => void;
  initialData?: AddressFormInitialData;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  countryName = 'country',
  cityName = 'city',
  postcodeName = 'postcode',
  addressName = 'address',
  stateName = 'state',
  resetFormField,
  initialData,
}) => {
  const { t } = useTranslation();
  const [showStateTooltip, setShowStateTooltip] = useState(false);
  const [showCityTooltip, setShowCityTooltip] = useState(false);
  const [countryIso, setCountryIso] = useState<string | undefined>(undefined);
  const [stateIso, setStateIso] = useState<string | undefined>(undefined);
  const isInitialCountryDataLoaded = useRef(false);
  const isInitialStateDataLoaded = useRef(false);

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    // only run once when the country data is loaded
    if (
      countries &&
      initialData?.country &&
      !isInitialCountryDataLoaded.current
    ) {
      const selectedCountry = countries.find(
        (country) => country.name === initialData.country
      );
      if (selectedCountry) {
        setCountryIso(selectedCountry.iso);
        isInitialCountryDataLoaded.current = true;
      }
    }
  }, [countries, initialData?.country]);

  const { data: states, isLoading: isLoadingStates } = useQuery(
    [EQueryKeys.STATES, countryIso],
    async () => {
      if (!countryIso) return;
      return countryStateCityApi.getStatesByCountry({
        countryIso,
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    // only run once when the state data is loaded
    if (states && initialData?.state && !isInitialStateDataLoaded.current) {
      const selectedState = states.find(
        (state) => state.name === initialData.state
      );
      if (selectedState) {
        setStateIso(selectedState.iso);
        isInitialStateDataLoaded.current = true;
      }
    }
  }, [states, initialData?.state]);

  const { data: cities, isLoading: isLoadingCities } = useQuery(
    [EQueryKeys.CITIES, countryIso, stateIso],
    async () => {
      if (!countryIso) return;
      return countryStateCityApi.getCitiesByStateAndCountry({
        countryIso,
        stateIso,
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  return (
    <>
      <FormLayoutGroup mdColumns={1}>
        <Form.Item name={addressName} label={t('general.address')}>
          <Input
            size="large"
            placeholder={t('general.enter', {
              field: t('general.address'),
            })}
          />
        </Form.Item>
      </FormLayoutGroup>
      <FormLayoutGroup>
        <Form.Item
          name={countryName}
          label={t('general.country')}
          className="mb-4"
        >
          <SortedSelect
            allowClear
            showSearch
            size="large"
            placeholder={t('general.selectField', {
              field: t('general.country'),
            })}
            loading={isLoadingCountries}
            suffixIcon={<ArrowDownIcon />}
            className="w-full"
            onChange={(value) => {
              const selectedCountry = countries?.find(
                (country) => country.name === value
              );
              setCountryIso(selectedCountry?.iso);
              resetFormField(stateName);
              resetFormField(cityName);
            }}
            options={uniqBy(countries || [], (country) => country.name).map(
              (country) => ({
                value: country.name,
                label: `${country.emoji} ${country.name}`,
                key: country.id,
              })
            )}
            optionFilterProp="value"
          ></SortedSelect>
        </Form.Item>

        <Tooltip
          title={t('forms.selectCountryFirst')}
          open={showStateTooltip}
          placement="bottom"
        >
          <Form.Item
            name={stateName}
            label={t('general.state')}
            className="mb-4"
          >
            <SortedSelect
              allowClear
              showSearch
              size="large"
              placeholder={t('general.selectField', {
                field: t('general.state'),
              })}
              loading={isLoadingStates}
              disabled={!countryIso}
              suffixIcon={<ArrowDownIcon />}
              className="w-full"
              onChange={(value) => {
                const selectedState = states?.find(
                  (state) => state.name === value
                );
                setStateIso(selectedState?.iso);
                resetFormField(cityName);
              }}
              options={uniqBy(states || [], (state) => state.name).map(
                (state) => ({
                  value: state.name,
                  label: state.name,
                  key: state.id,
                })
              )}
              optionFilterProp="value"
              onMouseEnter={() => setShowStateTooltip(!countryIso)}
              onMouseLeave={() => setShowStateTooltip(false)}
            ></SortedSelect>
          </Form.Item>
        </Tooltip>

        <Tooltip
          title={t('forms.selectCountryFirst')}
          open={showCityTooltip}
          placement="bottom"
        >
          <Form.Item name={cityName} label={t('general.city')} className="mb-4">
            <SortedSelect
              allowClear
              showSearch
              size="large"
              placeholder={t('general.selectField', {
                field: t('general.city'),
              })}
              loading={isLoadingCities}
              disabled={!countryIso}
              suffixIcon={<ArrowDownIcon />}
              className="w-full"
              options={uniqBy(cities || [], (city) => city.name).map(
                (city) => ({
                  value: city.name,
                  label: city.name,
                  key: city.id,
                })
              )}
              optionFilterProp="value"
              onMouseEnter={() => setShowCityTooltip(!countryIso)}
              onMouseLeave={() => setShowCityTooltip(false)}
            ></SortedSelect>
          </Form.Item>
        </Tooltip>

        <Form.Item name={postcodeName} label={t('forms.postalZipCode')}>
          <Input size="large" placeholder={t('forms.postal')} />
        </Form.Item>
      </FormLayoutGroup>
    </>
  );
};
